/**
 * Platform-agnostic health data reader seam.
 *
 * All downstream code (sync layer, future scoring) depends on this interface
 * only — never on `@capgo/capacitor-health` (HealthKit) or a Health Connect
 * SDK directly. Adding Android support later is a reader swap, not a rebuild.
 */

export type HealthPlatform = 'healthkit' | 'healthconnect' | 'none';

export type HealthScope = 'sleep' | 'restingHR' | 'hrv' | 'steps' | 'activeEnergy' | 'vo2Max';

export const CORE_HEALTH_SCOPES: HealthScope[] = [
  'sleep',
  'restingHR',
  'hrv',
  'steps',
  'activeEnergy',
];

export interface DateRange {
  start: Date;
  end: Date;
}

/**
 * A single day's value for one metric.
 *
 * `value = null` means "no reading that day" (device off, no sleep record,
 * etc.). It never means zero. Downstream code MUST distinguish "no data"
 * from "zero" and write `null` into the corresponding column.
 */
export interface DailyMetric {
  /** Local YYYY-MM-DD. */
  date: string;
  value: number | null;
}

/**
 * Per-scope probe outcome (data-presence diagnostic only — NOT the
 * source of truth for whether a scope is granted; use `AuthStatus` for
 * that).
 *   - `granted+data`   probe resolved with ≥1 sample.
 *   - `granted+empty`  probe resolved with 0 samples on device.
 *   - `threw`          probe threw (identifier wrong or denied).
 */
export type ProbeOutcome = 'granted+data' | 'granted+empty' | 'threw';

export interface ProbeResult {
  result: ProbeOutcome;
  sampleCount?: number;
  error?: string;
}

/**
 * iOS `Health.checkAuthorization` result per scope. Kept as a separate
 * signal from the data-presence probe so we can distinguish
 * "authorized but no data" vs "denied" vs "not determined" vs "unknown"
 * on the debug strip.
 */
export type AuthStatus = 'authorized' | 'denied' | 'notDetermined' | 'unknown';

export interface PermissionResult {
  /**
   * Source of truth: scopes where `checkAuthorization` returned
   * `authorized`. iOS may return `notDetermined` even post-prompt for
   * privacy reasons, so we intentionally do NOT fall back to the probe
   * result here — keeping both signals uncollapsed while we diagnose.
   */
  granted: HealthScope[];
  /** Per-scope `checkAuthorization` result. */
  authStatus?: Partial<Record<HealthScope, AuthStatus>>;
  /** Per-scope data-presence probe result (diagnostic only). */
  probes?: Partial<Record<HealthScope, ProbeResult>>;
}

/**
 * A getter result: aggregated daily metrics plus the raw sample count the
 * native side actually returned (pre-`fillMissingDays`). The raw count is
 * the debug-strip signal — the aggregated array length is always
 * "days in range" once filled, not "rows from device".
 */
export interface DailyMetricResult {
  metrics: DailyMetric[];
  /** Raw samples returned by the native plugin before aggregation. */
  sampleCount: number;
}

export interface HealthDataReader {
  readonly platform: HealthPlatform;

  isAvailable(): boolean;

  requestPermissions(scopes: HealthScope[]): Promise<PermissionResult>;

  getSleep(range: DateRange): Promise<DailyMetricResult>;
  getRestingHR(range: DateRange): Promise<DailyMetricResult>;
  getHRV(range: DateRange): Promise<DailyMetricResult>;
  getSteps(range: DateRange): Promise<DailyMetricResult>;
  getActiveEnergy(range: DateRange): Promise<DailyMetricResult>;
  /**
   * VO₂ max — sparse (Apple Watch estimates occasionally). Implementations
   * typically query a wide window (e.g. last 365 days) and return the most
   * recent sample as a single-entry `DailyMetricResult`. Empty result means
   * "no reading available".
   */
  getVO2Max(range: DateRange): Promise<DailyMetricResult>;
}
